import { supabase } from "@/supabase";
import { ref } from "vue";
import { getPagination } from "../utils/general";
import { StorageService } from "./storage.service";
import { storage_url } from "../utils/general";

const _BRAND = "brand";
export const _DEFAULT_PERMISSIONS = {
 discovery: false,
};

// Ordered list of client protocol sections shown as tabs in the Brand dialog.
// `key` is the stable storage key (do not rename), `label` is the UI label.
export const CLIENT_PROTOCOL_SECTIONS = [
 { key: "client_brief", label: "Client brief" },
 { key: "account_setup_guide", label: "Account set up guide" },
 { key: "financials_proposals", label: "Financials and proposals" },
 { key: "samples", label: "Samples" },
 { key: "selection_negotiations", label: "Selection and negotiations" },
 { key: "talent_brief", label: "Talent brief" },
 { key: "contracts", label: "Contracts" },
 { key: "operations_deliveries", label: "Operations and deliveries" },
 { key: "quotes_pos", label: "Quote and POs" },
 { key: "talent_scripts_guide", label: "Talent individual scripts guide" },
 { key: "content_stage", label: "Content stage" },
 { key: "content_approval", label: "Content approval" },
 { key: "content_publishing", label: "Content publishing" },
 { key: "store_visits_events", label: "Store visits & events" },
 { key: "reports", label: "Reports" },
 { key: "campaign_completion", label: "Campaign completion" },
];

// Scope decides whether a brand uses its own protocols or the company's shared ones.
export const CLIENT_PROTOCOL_SCOPE = { BRAND: "brand", COMPANY: "company" };

// Returns a fresh protocols object with every section initialised to empty string,
// merging any existing values passed in. Accepts either an object or a JSON
// string (the column may come back as text/double-encoded JSON from the API).
export const buildClientProtocols = (existing = {}) => {
 let source = existing;
 if (typeof source === "string") {
  try {
   source = JSON.parse(source);
  } catch (e) {
   source = {};
  }
 }
 if (!source || typeof source !== "object") {
  source = {};
 }
 const base = {};
 for (const section of CLIENT_PROTOCOL_SECTIONS) {
  base[section.key] = (source && source[section.key]) || "";
 }
 return base;
};

// True if a protocols object has any non-empty section (ignoring HTML tags).
export const hasClientProtocolContent = (protocols) => {
 const p = buildClientProtocols(protocols);
 return CLIENT_PROTOCOL_SECTIONS.some(
  (section) => (p[section.key] || "").replace(/<(.|\n)*?>/g, "").trim().length > 0
 );
};

// Decides whether a brand should use its own protocols or the company's shared
// ones. Defaults to "This brand"; uses "Whole company" only when the company has
// protocols of its own (and the brand hasn't been given its own content).
//   1. Brand explicitly brand-scoped AND has its own content -> brand
//   2. Otherwise, if the company has any protocol content       -> company
//   3. Otherwise                                                -> brand
export const resolveClientProtocolScope = (brand) => {
 if (
  brand &&
  brand.client_protocols_scope === CLIENT_PROTOCOL_SCOPE.BRAND &&
  hasClientProtocolContent(brand.client_protocols)
 ) {
  return CLIENT_PROTOCOL_SCOPE.BRAND;
 }
 if (brand && hasClientProtocolContent(brand.company && brand.company.client_protocols)) {
  return CLIENT_PROTOCOL_SCOPE.COMPANY;
 }
 return CLIENT_PROTOCOL_SCOPE.BRAND;
};

export const subscribedBrand = ref({});
export const setBrandSubscription = (brand) => {
  subscribedBrand.value = brand;
}
export const BrandService = {
 async getBrand(id) {
  const resp = { brand: null, error: null };
  const { data: brand, error } = await supabase
   .from(_BRAND)
   .select("*,company!inner(*)")
   .eq("id", id)
   .single();
  if (error) {
   resp.error = error.message;
   return resp;
  }
  if (brand) {
   if (brand.logo) {
    if (!brand.logo.startsWith('http')) {
        brand.logo = storage_url + brand.logo;
    }   
   }
   if (brand.permissions == null) {
    brand.permissions = _DEFAULT_PERMISSIONS;
   }
   if (brand.company?.logo) {
    if (!brand.company.logo.startsWith('http')) {
        brand.company.logo = storage_url + brand.company.logo;
    }   
   }
   resp.brand = brand;
   return resp;
  }
  return resp;
 },
 async getBrands(filter = {}, orderBy = {}, page) {
  const brands = { data: null, count: 0, error: null };
  try {
   const query = supabase
    .from(_BRAND)
    .select("*,company!inner(*)", { count: "exact" });
   if (filter.search_brand_name) {
    query.filter("brand_name", "ilike", `%${filter.search_brand_name}%`);
   }
   if (filter.company_id) {
    query.eq("company_id", filter.company_id);
   }
   if (filter.search_company_name) {
    query.filter(
     "company.company_name",
     "ilike",
     `%${filter.search_company_name}%`
    );
   }
   if (orderBy && orderBy.field) {
    query.order(orderBy.field, {
     ascending: orderBy.order === "asc" ? true : false,
    });
   }
   if (page > -1) {
    const { from, to } = getPagination(page, 20);
    query.range(from, to);
   }
   const { data, count, error } = await query;
   if (error) {
    brands.error = error.message;
    return brands;
   }
   if (data == null) {
    return brands;
   }
   for (const brand of data) {
    // if (brand.social_media) {
    //      brand.social_media = brand.social_media;
    // }
    if (brand.logo) {
        if (!brand.logo.startsWith('http')) {
            brand.logo = storage_url + brand.logo;
        }   
    }
    if (brand.company.logo) {
        if (!brand.company.logo.startsWith('http')) {
            brand.company.logo = storage_url + brand.company.logo;
        }   
    }
    if (brand.permissions == null) {
    //     console.log(brand.permissions);
    // } else {
        brand.permissions = _DEFAULT_PERMISSIONS;
    }
   }
   brands.data = data;
   brands.count = count;
   return brands;
  } catch (err) {
   brands.error = err.message;
   return brands;
  }
 },
 async addBrand(data) {
  const resp = { created: false, error: null };
  const { data: brandObj, error: brandObjError } = await this._getBrandObject(
   data
  );
  if (brandObjError) {
   resp.error = brandObjError;
   return resp;
  }
  if (brandObj) {
   const { error: brandError } = await supabase
    .from(_BRAND)
    .insert(brandObj)
    .single();
   if (brandError) {
    resp.error = brandError.message;
    return resp;
   }
   resp.created = true;
   return resp;
  }
 },
 async updateBrand(data) {
  const resp = { updated: false, error: null };
  const { data: brandObj, error: brandObjError } = await this._getBrandObject(
   data
  );

  if (brandObjError) {
   resp.error = brandObjError;
   return resp;
  }
  if (brandObj) {
   const { error: brandError } = await supabase
    .from(_BRAND)
    .update(brandObj)
    .eq("id", data.id)
    .single();

   if (brandError) {
    resp.error = brandError;
    return resp;
   }
  }
  resp.updated = true;
  return resp;
 },
 async _getBrandObject(data) {
  const resp = { data: null, error: null };
  if (data.logo_file) {
   const { data: file, error } = await StorageService.upload(
    data.logo_file,
    "logo"
   );
   if (error) {
    resp.error = error.message;
    return resp;
   }
   if (file) {
    if (data) {
     data.logo = file.path;
    }
   }
  }

  const brandObj = {};
  if ("brand_name" in data) brandObj.brand_name = data.brand_name;
  if ("company_id" in data) brandObj.company_id = data.company_id;
  if ("logo" in data) brandObj.logo = data.logo;
  if ("website" in data) brandObj.website = data.website;
  if ("is_dashboard_customized" in data)
   brandObj.is_dashboard_customized = data.is_dashboard_customized;
  if ("color" in data) brandObj.color = data.color;
  if ("social_media" in data)
   brandObj.social_media = data.social_media;
  if ("permissions" in data) brandObj.permissions = data.permissions;
  if ("client_protocols" in data)
   brandObj.client_protocols = data.client_protocols;
  if ("client_protocols_scope" in data)
   brandObj.client_protocols_scope = data.client_protocols_scope;
  resp.data = brandObj;
  return resp;
 },
};
